import { InputHTMLAttributes, ReactNode, forwardRef } from 'react';
import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

/*
 * SearchBar — DESIGN_SYSTEM.md §2.1
 *
 * Unified search input shape used across admin/staff list pages. Wraps the
 * design-system <Input> with a leading search icon, and reserves an
 * `actions` slot for adjacent buttons (Go to date, sort toggle, etc.).
 *
 *   <SearchBar
 *     value={q}
 *     onChange={(e) => setQ(e.target.value)}
 *     placeholder="Search bookings"
 *     actions={<Button …>Go to date</Button>}
 *   />
 */

export interface SearchBarProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Buttons rendered to the right of the input (date picker, sort, etc.). */
  actions?: ReactNode;
  /** Container className (the flex row, not the input). */
  containerClassName?: string;
  /** Override default leading icon. */
  leadingIcon?: ReactNode;
}

const SearchBar = forwardRef<HTMLInputElement, SearchBarProps>(
  ({ actions, containerClassName, leadingIcon, className, ...props }, ref) => {
    return (
      <div className={cn('flex flex-col gap-2 sm:flex-row sm:items-center', containerClassName)}>
        <div className="flex-1 min-w-0">
          <Input
            ref={ref}
            type="search"
            leading={leadingIcon ?? <Search className="h-4 w-4" />}
            className={className}
            {...props}
          />
        </div>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>
    );
  },
);
SearchBar.displayName = 'SearchBar';

export { SearchBar };
